import { ArrowDown } from 'lucide-react';

import {
  CampusThalwilEvents,
  EventsSocialProgrammes,
  InternationalOffice,
} from '@/components/current-students';
import {
  MediaFrame,
  NavyAtmosphere,
  PointerParallax,
  ScrollReveal,
  SplitReveal,
  TextStagger,
  TiltCard,
} from '@/components/motion';
import { EnquiryForm } from '@/components/sections';
import { Button } from '@ibas/ui';

/**
 * Real IBAS asset: a student relaxing in a warm, softly lit campus lounge. A
 * portrait orientation is used so the figure fills the 4/5 hero plate under
 * object-cover. A still is used deliberately — the page carries its motion
 * through the sections rather than an autoplaying hero loop.
 */
const HERO_POSTER = '/media/images/people/student-lounge-cocktail-portrait.webp';

/**
 * CurrentStudentsPage (/current-students) — "Current Students". A navy,
 * motion-led hero opens the page, then the three requested blocks follow in
 * order: International Students Office · Events & Social Programmes · Campus
 * Thalwil Events. The shared enquiry form closes the page.
 *
 * Each in-page section is anchored (id + scroll-mt) so the mega-nav can
 * deep-link to it. Every section is a self-contained, reduced-motion-safe
 * component reading only verified facts from @/data/current-students.
 */
export function CurrentStudentsPage() {
  return (
    <div className="bg-background">
      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative isolate flex min-h-[88vh] items-center overflow-hidden bg-brand-navy">
        <NavyAtmosphere />

        <div className="relative z-10 mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-12 px-6 py-20 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
          <div className="max-w-xl">
            <TextStagger
              text="Current Students"
              className="font-label text-xs tracking-[0.24em] text-brand-gold-light uppercase"
            />
            <SplitReveal
              as="h1"
              by="lines"
              className="mt-4 font-display text-4xl leading-[1.05] text-white md:text-6xl"
            >
              Everything you need, once you’re here.
            </SplitReveal>
            <ScrollReveal from="up" delay={0.1}>
              <p className="mt-6 max-w-lg text-lg leading-relaxed text-white/70">
                From the International Students Office to social programmes and on-site events in
                Thalwil, IBAS supports you throughout your studies — practically, personally and as
                part of a global community.
              </p>
            </ScrollReveal>
            <ScrollReveal from="up" delay={0.2}>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white"
                  asChild
                >
                  <a href="#international-office">
                    Student support &amp; services
                    <ArrowDown aria-hidden className="size-4" />
                  </a>
                </Button>
              </div>
            </ScrollReveal>
          </div>

          {/* Hero media plate — pointer parallax over a tilt-reactive frame. */}
          <PointerParallax strength={16} className="mx-auto w-full max-w-md lg:max-w-none">
            <TiltCard className="rounded-2xl" glare>
              <div data-parallax data-depth="0.8">
                <MediaFrame
                  image={HERO_POSTER}
                  alt="An IBAS student relaxing with a drink in a warm, softly lit campus lounge."
                  aspect="aspect-[4/5]"
                  overlay={55}
                  rounded="rounded-2xl"
                  position="50% 40%"
                >
                  <div className="absolute inset-x-6 bottom-6 md:inset-x-8 md:bottom-8">
                    <span className="font-label text-xs tracking-[0.2em] text-brand-gold-light uppercase">
                      Study Your Way. Connect Globally.
                    </span>
                    <p className="mt-1 font-display text-2xl leading-tight text-white md:text-3xl">
                      Supported, every step.
                    </p>
                  </div>
                </MediaFrame>
              </div>
            </TiltCard>
          </PointerParallax>
        </div>
      </section>

      {/* ── 1 · INTERNATIONAL STUDENTS OFFICE ─────────────────────────────── */}
      <div id="international-office" className="scroll-mt-24">
        <InternationalOffice />
      </div>

      {/* ── 2 · EVENTS & SOCIAL PROGRAMMES ────────────────────────────────── */}
      <div id="events" className="scroll-mt-24">
        <EventsSocialProgrammes />
      </div>

      {/* ── 3 · CAMPUS THALWIL EVENTS ─────────────────────────────────────── */}
      <div id="campus-events" className="scroll-mt-24">
        <CampusThalwilEvents />
      </div>

      {/* Shared enquiry form closes the page. */}
      <EnquiryForm />
    </div>
  );
}
